import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { confirmDelete, showError, toast } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useAuth } from '../../context/AuthContext';
import { formatMoney, formatDate } from '../../utils/format';

export default function DebitNotes() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', status: '', per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.debitNotes.list(params);
      const pg = data.debit_notes || data;
      setRows(pg.data || []);
      setMeta({ current_page: pg.current_page, last_page: pg.last_page, total: pg.total, per_page: pg.per_page });
    } catch (err) {
      showError('Failed to load debit notes', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete(`debit note ${row.note_no}`);
    if (!res.isConfirmed) return;
    try {
      await api.debitNotes.remove(row.id);
      toast('success', '', 'Debit note deleted');
      load();
    } catch (err) {
      showError('Failed to delete', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Debit Notes"
        subtitle="Purchase returns and supplier debits."
        crumbs={[{ label: 'Finance', to: '/invoices' }, { label: 'Debit Notes' }]}
        actions={hasPermission('create-invoices') && (
          <Link to="/debit-notes/new" className="btn btn-primary"><FiPlus /> New Debit Note</Link>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <input
              className="form-control"
              style={{ maxWidth: 240 }}
              placeholder="Search note no, party, reason..."
              value={params.search}
              onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })}
            />
            <SearchableSelect
              options={[
                { value: '', label: 'All Status' },
                { value: 'open', label: 'Open' },
                { value: 'used', label: 'Posted' },
                { value: 'void', label: 'Void' },
              ]}
              value={params.status}
              onChange={(v) => setParams({ ...params, status: v, page: 1 })}
              placeholder="All Status"
            />
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="debit_notes" label="debit notes" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No debit notes found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'note_no', label: 'Note No', render: (r) => <b>{r.note_no}</b> },
              { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
              { key: 'party', label: 'Supplier / Party', render: (r) => r.supplier?.name || r.party?.name || '-' },
              { key: 'amount', label: 'Amount', align: 'right', render: (r) => formatMoney(r.amount) },
              { key: 'reason', label: 'Reason' },
              { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status === 'used' ? 'posted' : r.status} /> },
              { key: 'actions', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {r.voucher_id && (
                    <Link to={`/vouchers/${r.voucher_id}`} className="btn btn-sm btn-secondary">Voucher</Link>
                  )}
                  {!r.voucher_id && hasPermission('update-invoices') && (
                    <Link to={`/debit-notes/${r.id}/edit`} className="btn btn-sm btn-secondary">Edit</Link>
                  )}
                  {!r.voucher_id && hasPermission('delete-invoices') && (
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r)}>Delete</button>
                  )}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}
